<template>
  <transition name="crm-reminder">
    <aside v-if="activeReminder" class="crm-task-reminder">
      <div class="crm-task-reminder__header">
        <span>{{ reminderHeader }}</span>
        <button type="button" title="Fechar" @click="dismissCurrent">
          <i class="la la-times"></i>
        </button>
      </div>

      <strong>{{ activeReminder.titulo }}</strong>
      <p>{{ reminderContext }}</p>

      <div class="crm-task-reminder__time">
        <i class="la la-clock"></i>
        {{ formatDateTime(reminderDate) }}
      </div>

      <div class="crm-task-reminder__actions">
        <button
          class="btn btn-light btn-sm"
          type="button"
          @click="openReminder"
        >
          Abrir
        </button>
        <button
          v-if="canComplete"
          class="btn btn-light-success btn-sm"
          type="button"
          :disabled="saving"
          @click="completeTask"
        >
          Concluir
        </button>
        <button
          v-else
          class="btn btn-light btn-sm"
          type="button"
          :disabled="saving"
          @click="dismissCurrent"
        >
          Fechar
        </button>
      </div>

      <div v-if="canSnooze" class="crm-task-reminder__snooze">
        <b-form-input
          v-model.number="snoozeMinutes"
          min="1"
          max="1440"
          type="number"
        />
        <button
          class="btn btn-dark btn-sm"
          type="button"
          :disabled="saving"
          @click="snoozeTask"
        >
          Lembrar depois
        </button>
      </div>
    </aside>
  </transition>
</template>

<script>
import crmService from "@/modules/CRM/services/crmService";

const POLL_INTERVAL = 60000;

export default {
  name: "CrmTaskReminder",
  data() {
    return {
      dismissed: {},
      polling: null,
      reminders: [],
      saving: false,
      snoozeMinutes: 10,
      stopped: false,
    };
  },
  computed: {
    activeReminder() {
      return (
        this.reminders.find(
          (item) => !this.dismissed[this.reminderKey(item)]
        ) || null
      );
    },
    canComplete() {
      return (
        this.activeReminder &&
        this.activeReminder.tipo_lembrete === "atividade" &&
        this.activeReminder.pode_concluir !== false
      );
    },
    canSnooze() {
      return (
        this.activeReminder &&
        this.activeReminder.tipo_lembrete === "atividade" &&
        this.activeReminder.pode_adiar !== false
      );
    },
    reminderDate() {
      if (!this.activeReminder) {
        return null;
      }

      return (
        this.activeReminder.data_lembrete ||
        this.activeReminder.vencimento_em ||
        this.activeReminder.data_inicio ||
        this.activeReminder.created_at
      );
    },
    reminderHeader() {
      const type = this.activeReminder?.tipo_lembrete || "atividade";

      if (type === "agenda_10_min") {
        return "Agendamento em 10 min";
      }

      if (type === "notificacao_agenda_link") {
        return "Cliente agendou";
      }

      if (type === "notificacao_funil") {
        return "Novo card no funil";
      }

      return "Tarefa agora";
    },
    reminderContext() {
      if (!this.activeReminder) {
        return "";
      }

      return (
        this.activeReminder.descricao ||
        this.activeReminder.oportunidade ||
        this.activeReminder.contato ||
        "Sem oportunidade ou contato"
      );
    },
  },
  mounted() {
    this.loadReminders();
    this.polling = setInterval(() => this.loadReminders(), POLL_INTERVAL);
  },
  beforeDestroy() {
    clearInterval(this.polling);
  },
  methods: {
    async loadReminders() {
      if (this.stopped) {
        return;
      }

      try {
        const { data } = await crmService.lembretesAtividades({
          janela_minutos: 10,
        });
        this.reminders = this.normalizeCollection(data);
      } catch (error) {
        const status = error?.response?.status;

        if (status === 401 || status === 403) {
          this.stopped = true;
          clearInterval(this.polling);
          return;
        }

        this.reminders = [];
      }
    },
    async dismissCurrent() {
      if (!this.activeReminder) {
        return;
      }

      const reminder = this.activeReminder;
      const key = this.reminderKey(reminder);

      this.$set(this.dismissed, key, true);
      this.removeReminder(key, false);

      if (reminder.id_notificacao) {
        await this.markNotificationAsRead(reminder.id_notificacao);
      }
    },
    async openReminder() {
      if (!this.activeReminder) {
        return;
      }

      const reminder = this.activeReminder;

      if (reminder.id_notificacao) {
        await this.dismissCurrent();
      }

      if (reminder.id_crm_oportunidade) {
        this.$router.push({
          name: "crmOportunidadeDetalhe",
          params: { id: reminder.id_crm_oportunidade },
          query: { origem: this.routeOrigin(reminder) },
        });
        return;
      }

      if (reminder.action) {
        this.openAction(reminder.action);
        return;
      }

      if (reminder.id_crm_agenda) {
        this.$router.push({ name: "crmAgenda" });
        return;
      }

      this.$router.push({ name: "crmTarefas" });
    },
    async completeTask() {
      if (!this.activeReminder || !this.canComplete) {
        return;
      }

      const reminder = this.activeReminder;

      this.saving = true;
      try {
        await crmService.concluirAtividade({
          id_crm_atividade: reminder.id_crm_atividade,
        });
        this.removeReminder(this.reminderKey(reminder));
        this.$toast.success("Tarefa concluída.");
      } finally {
        this.saving = false;
      }
    },
    async snoozeTask() {
      if (!this.activeReminder || !this.canSnooze) {
        return;
      }

      const reminder = this.activeReminder;
      const minutos = Math.min(
        Math.max(Number(this.snoozeMinutes || 10), 1),
        1440
      );

      this.saving = true;
      try {
        await crmService.adiarLembreteAtividade({
          id_crm_atividade: reminder.id_crm_atividade,
          minutos,
        });
        this.removeReminder(this.reminderKey(reminder));
        this.$toast.success("Lembrete adiado.");
      } finally {
        this.saving = false;
      }
    },
    removeReminder(key, clearDismissed = true) {
      this.reminders = this.reminders.filter(
        (item) => this.reminderKey(item) !== key
      );

      if (clearDismissed) {
        this.$delete(this.dismissed, key);
      }
    },
    normalizeCollection(data) {
      if (Array.isArray(data)) {
        return data.map((item) => this.normalizeReminder(item));
      }

      if (data && Array.isArray(data.data)) {
        return data.data.map((item) => this.normalizeReminder(item));
      }

      return [];
    },
    normalizeReminder(item) {
      return {
        tipo_lembrete: item.tipo_lembrete || "atividade",
        lembrete_key:
          item.lembrete_key ||
          (item.id_crm_atividade
            ? `atividade:${item.id_crm_atividade}`
            : `reminder:${JSON.stringify(item)}`),
        ...item,
      };
    },
    reminderKey(item) {
      return (
        item?.lembrete_key || item?.id_crm_atividade || item?.id_notificacao
      );
    },
    routeOrigin(reminder) {
      if (reminder.tipo_lembrete === "agenda_10_min") {
        return "agenda";
      }

      if (reminder.tipo_lembrete === "notificacao_funil") {
        return "funil";
      }

      return "tarefas";
    },
    openAction(action) {
      const internal = String(action)
        .replace(/^https?:\/\/[^/]+/i, "")
        .replace(/^\/?#/, "");

      if (internal.startsWith("/")) {
        this.$router.push(internal);
        return;
      }

      window.location.href = action;
    },
    async markNotificationAsRead(id) {
      try {
        await crmService.marcarNotificacaoVisualizada({ id });
      } catch (error) {
        this.$toast.error(
          "Não foi possível marcar a notificação como visualizada."
        );
      }
    },
    formatDateTime(value) {
      return value ? new Date(value).toLocaleString("pt-BR") : "-";
    },
  },
};
</script>

<style scoped>
.crm-task-reminder {
  background: #ffffff;
  border: 1px solid #dbe3ef;
  border-left: 4px solid #0bb7d4;
  border-radius: 8px;
  bottom: 24px;
  box-shadow: 0 18px 42px rgba(23, 31, 56, 0.18);
  color: #25324b;
  max-width: calc(100vw - 32px);
  padding: 14px;
  position: fixed;
  right: 24px;
  width: 340px;
  z-index: 1080;
}

.crm-task-reminder__header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.crm-task-reminder__header span {
  color: #0b87c9;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
}

.crm-task-reminder__header button {
  align-items: center;
  background: #f3f6fb;
  border: 0;
  border-radius: 4px;
  color: #647084;
  display: inline-flex;
  height: 28px;
  justify-content: center;
  width: 28px;
}

.crm-task-reminder strong {
  display: block;
  font-size: 15px;
  font-weight: 900;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.crm-task-reminder p {
  color: #647084;
  font-size: 13px;
  margin: 5px 0 0;
  overflow-wrap: anywhere;
}

.crm-task-reminder__time {
  align-items: center;
  background: #f3f6fb;
  border-radius: 6px;
  display: flex;
  font-size: 12px;
  font-weight: 800;
  gap: 6px;
  margin-top: 10px;
  padding: 8px 10px;
}

.crm-task-reminder__actions,
.crm-task-reminder__snooze {
  display: grid;
  gap: 8px;
  margin-top: 10px;
}

.crm-task-reminder__actions {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.crm-task-reminder__snooze {
  grid-template-columns: 88px minmax(0, 1fr);
}

.crm-task-reminder__snooze input {
  min-height: 34px;
}

.crm-reminder-enter-active,
.crm-reminder-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.crm-reminder-enter,
.crm-reminder-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

@media (max-width: 575px) {
  .crm-task-reminder {
    bottom: 12px;
    right: 12px;
    width: calc(100vw - 24px);
  }
}
</style>
